/**
 * ProgramOverview — the program "Overview" tab (DEV-408).
 *
 * Composes the redesign foundation (semantic tokens + Aeonik) into the Overview
 * frame from Figma: a main column (program description, milestone rail, a hero
 * agent input) and a right rail (What's next + Recent Updates).
 *
 * The page body only — breadcrumb / title / tabs are owned by ProgramLayout +
 * AppShell. Data is surfaced from the existing program detail endpoint; the
 * milestone / what's-next state is derived from that payload.
 */

import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Paperclip,
  Send,
  ChevronRight,
  FileText,
  Sparkles,
  CheckCircle2,
} from 'lucide-react'
import { tokenManager } from '../../lib/api'
import { useAgent } from '../../contexts/AgentContext'
import { useViewportContext } from '../../hooks/useViewportContext'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

interface OutputDocument {
  id: number
  title: string
  template_name?: string
  created_at: string
}

interface ProgramDetail {
  id: number
  name: string
  description?: string
  status?: string
  status_display?: string
  technology?: number | null
  selected_technologies?: unknown[]
  requirement_profile?: { id: number } | null
  submissions_count?: number
  output_documents?: OutputDocument[]
  created_at?: string
  updated_at?: string
}

interface Milestone {
  key: string
  label: string
  done: boolean
  route: string
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso?: string): string {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const minutes = Math.round(diff / 60000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}hr ago`
  const days = Math.round(hours / 24)
  if (days < 7) return `${days}d ago`
  const weeks = Math.round(days / 7)
  if (weeks < 5) return `${weeks}w ago`
  return new Date(iso).toLocaleDateString()
}

// ── Component ─────────────────────────────────────────────────────────────────

const ProgramOverview = () => {
  const { programId } = useParams<{ programId: string }>()
  const navigate = useNavigate()
  const { openDrawerWith, updatePageContext } = useAgent()

  const [program, setProgram] = useState<ProgramDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState('')

  useEffect(() => {
    updatePageContext({ currentView: 'program_overview' })
  }, [updatePageContext])

  useEffect(() => {
    if (!programId) return
    let cancelled = false

    const load = async () => {
      setLoading(true)
      try {
        const token = tokenManager.getAccessToken()
        const res = await fetch(`${API_BASE_URL}/api/programs/${programId}/`, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        })
        if (res.ok && !cancelled) {
          setProgram(await res.json())
        }
      } catch (err) {
        console.error('Failed to load program overview:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [programId])

  // Derive the lifecycle milestones from the program payload (obvious surfacing
  // of existing data — no dedicated milestone model exists yet).
  const milestones: Milestone[] = useMemo(() => {
    const base = `/programs/${programId}`
    const techCount = program?.selected_technologies?.length ?? 0
    return [
      {
        key: 'requirements',
        label: 'Define Requirements',
        done: !!program?.requirement_profile,
        route: `${base}/requirements`,
      },
      {
        key: 'taxonomy',
        label: 'Solution taxonomy',
        done: techCount > 0 || !!program?.technology,
        route: `${base}/market`,
      },
      {
        key: 'vendors',
        label: 'Vendor Selection',
        done: (program?.submissions_count ?? 0) > 0,
        route: `${base}/vendors`,
      },
      {
        key: 'plan',
        label: 'Acquisition plan',
        done: (program?.output_documents?.length ?? 0) > 0,
        route: `${base}/setup/documents`,
      },
    ]
  }, [program, programId])

  const nextMilestone = useMemo(() => milestones.find((m) => !m.done), [milestones])

  // Recent activity — surfaced from generated output documents, newest first.
  const updates = useMemo(() => {
    const docs = [...(program?.output_documents ?? [])]
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 4)
      .map((doc) => ({
        id: `doc-${doc.id}`,
        icon: FileText,
        title: 'New document created',
        subtitle: doc.title || doc.template_name,
        ts: doc.created_at,
      }))

    if (docs.length > 0) return docs

    // Fallback so the rail is never empty for a fresh program.
    return [
      {
        id: 'created',
        icon: Sparkles,
        title: 'Program created',
        subtitle: program?.name,
        ts: program?.created_at,
      },
    ]
  }, [program])

  // Tier-2 viewport so the agent can answer about what's on screen.
  useViewportContext(
    'program_overview',
    () => ({
      view: 'program_overview',
      program: { id: program?.id, name: program?.name, status: program?.status_display },
      description: program?.description,
      milestones: milestones.map((m) => ({ label: m.label, done: m.done })),
      nextStep: nextMilestone?.label ?? null,
    }),
    [program, milestones, nextMilestone],
  )

  const handleStart = () => {
    const text = draft.trim()
    if (!text) return
    openDrawerWith(text)
    setDraft('')
  }

  if (loading) {
    return (
      <div className="px-6 py-10 lg:px-10">
        <div className="mx-auto max-w-[1400px] animate-pulse space-y-6">
          <div className="h-5 w-44 rounded-md bg-muted" />
          <div className="h-24 w-full max-w-3xl rounded-lg bg-muted" />
          <div className="h-40 w-full max-w-3xl rounded-xl bg-muted" />
        </div>
      </div>
    )
  }

  const description = program?.description?.trim()
  const nextInsight = nextMilestone
    ? `Next up: ${nextMilestone.label.toLowerCase()} to keep your acquisition moving forward.`
    : 'Every milestone is complete — start a new program, run a market search, and more.'

  return (
    <div className="px-6 py-10 lg:px-10">
      <div className="mx-auto flex max-w-[1400px] flex-col gap-12 lg:flex-row lg:gap-16">
        {/* ── Main column ─────────────────────────────────────────── */}
        <main className="flex min-w-0 flex-1 flex-col gap-16">
          {/* Program description */}
          <section className="flex flex-col gap-4">
            <h2 className="text-base font-medium text-foreground">Program description</h2>
            <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">
              {description || 'No description has been added for this program yet.'}
            </p>
          </section>

          {/* Milestones */}
          <section className="flex flex-col gap-4">
            <h2 className="text-base font-medium text-foreground">Milestones</h2>
            <div className="flex items-center gap-3">
              {milestones.map((m, i) => (
                <div key={m.key} className="flex min-w-0 flex-1 items-center gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(m.route)}
                    className="flex min-w-0 flex-1 flex-col items-start gap-1 rounded-md text-left transition-opacity hover:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <span className="text-sm text-foreground">
                      {m.done ? 'Completed' : 'Incomplete'}
                    </span>
                    <span className="text-sm font-medium text-foreground">{m.label}</span>
                  </button>
                  {i < milestones.length - 1 && (
                    <span className="h-px w-6 shrink-0 bg-border" aria-hidden />
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Hero agent input */}
          <section className="flex flex-col gap-3.5">
            <p className="text-sm text-muted-foreground">{nextInsight}</p>
            <div className="flex h-40 w-full flex-col rounded-xl border border-border bg-card px-6 pb-4 pt-6 shadow-xs">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleStart()
                  }
                }}
                placeholder="Start a new acquisition program, market search, and more..."
                className="flex-1 resize-none border-0 bg-transparent text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-0"
              />
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => openDrawerWith('')}
                  aria-label="Attach a file"
                  className="flex size-10 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Paperclip className="size-4" />
                </button>
                <button
                  type="button"
                  onClick={handleStart}
                  disabled={!draft.trim()}
                  aria-label="Send to agent"
                  className="flex size-10 items-center justify-center rounded-full bg-muted text-foreground transition-colors hover:bg-accent disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Send className="size-5" />
                </button>
              </div>
            </div>
          </section>
        </main>

        {/* ── Right rail ──────────────────────────────────────────── */}
        <aside className="flex w-full shrink-0 flex-col gap-10 lg:w-[397px]">
          {/* What's next */}
          <section className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-base font-medium text-foreground">What's next</h2>
              <p className="text-sm text-muted-foreground">
                Recommended actions to move your program forward.
              </p>
            </div>
            {nextMilestone ? (
              <button
                type="button"
                onClick={() => navigate(nextMilestone.route)}
                className="flex w-full items-center gap-4 rounded-md border border-border bg-muted p-4 text-left transition-colors hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Sparkles className="size-4 shrink-0 text-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-foreground">{nextMilestone.label}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    Complete this step to advance the program.
                  </p>
                </div>
                <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
              </button>
            ) : (
              <div className="flex w-full items-center gap-4 rounded-md border border-border bg-muted p-4">
                <CheckCircle2 className="size-4 shrink-0 text-success" />
                <p className="text-sm text-foreground">All milestones complete.</p>
              </div>
            )}
          </section>

          {/* Recent Updates */}
          <section className="flex flex-col gap-4">
            <h2 className="text-base font-medium text-foreground">Recent Updates</h2>
            <div className="flex flex-col gap-2">
              {updates.map((u) => {
                const Icon = u.icon
                return (
                  <div
                    key={u.id}
                    className="flex items-center justify-between gap-4 rounded-md bg-muted p-4"
                  >
                    <div className="flex min-w-0 items-center gap-4">
                      <Icon className="size-4 shrink-0 text-foreground" />
                      <div className="min-w-0">
                        <p className="truncate text-sm text-foreground">{u.title}</p>
                        {u.subtitle && (
                          <p className="truncate text-sm font-medium text-foreground">
                            {u.subtitle}
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                      {timeAgo(u.ts)}
                    </span>
                  </div>
                )
              })}
            </div>
          </section>
        </aside>
      </div>
    </div>
  )
}

export default ProgramOverview
